import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import './index.css';
import App from './App';
import { AuthProvider } from './context/AuthContext';
import { AppearanceProvider } from './context/AppearanceContext';
import { ToastProvider } from './components/ui/overlays';

/**
 * react-query is the server-state layer for the whole console.
 *
 * Defaults tuned for an operator console rather than a public app: the data is
 * other people's live records so it must not be cached hard, but every screen
 * is a filtered table and refetching on each window focus would hammer
 * cross-tenant aggregations that have no supporting index yet.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Never retry a real answer: 401/403 means sign in or you may not, 404
        // means it is gone, 400 means the request was wrong. Retrying only
        // helps a transient network or 5xx failure.
        const status = error?.status;
        if (status && status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AppearanceProvider>
        <BrowserRouter>
          <ToastProvider>
            <AuthProvider>
              <App />
            </AuthProvider>
          </ToastProvider>
        </BrowserRouter>
      </AppearanceProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
